import { Footer } from '../components/footer/footer';
import { Scroll } from '../components/scroll/scroll';
import { Header } from '../components/header/header';
import { useI18n } from '../i18n';
import { Button } from '../components/button/button';
import { ContentIndex } from '../components/content-index';
import { Gallery } from '../components/gallery/gallery';
import profileData from '../data/profile.json';
import './projects.scss';

type LocalizedField = string | { en?: string; pt?: string };

function localizeField(field: LocalizedField, language: 'en' | 'pt'): string {
  if (typeof field === 'string') {
    return field;
  }
  return field[language] ?? field.en ?? field.pt ?? '';
}

interface ProjectsInnerProps {
  translations: any;
  language: 'en' | 'pt';
  plurallItem: any;
  otherProjects: any[];
}

function ProjectImageGallery({
  images,
  projectTitle,
}: {
  images: string[];
  projectTitle: string;
}) {
  if (!images?.length) return null;

  return (
    <Gallery
      images={images.map((src, index) => ({
        src,
        alt: `${projectTitle} screenshot ${index + 1}`,
      }))}
    />
  );
}

function ProjectsInner({
  translations,
  language,
  plurallItem,
  otherProjects,
}: ProjectsInnerProps) {
  return (
    <>
      <Header
        title={translations.nav.projects}
        // description={projectsData.description}
        breadcrumbs={[
          { label: 'GAraujo', href: '/' },
          { label: translations.nav.projects, href: '/projects' },
        ]}
      />
      <ContentIndex />

      <div className="content projects-content">

        <section className="project-case">
          <article className="project-case__intro">
            <h2 id="risk-manager">{translations.projectPage.riskManagerTitle}</h2>
            <p>{translations.projectPage.riskManagerIntroP1}</p>
            <p>{translations.projectPage.riskManagerIntroP2}</p>
          </article>
        </section>

        <Gallery
          images={[
            { src: '/img/projects/riskmanager/garaujo.space portfolio riskmanager details 2009.png', alt: 'Risk Manager details 2009' },
            { src: '/img/projects/riskmanager/garaujo.space portfolio modulo logo.png', alt: 'Módulo logo' },
            { src: '/img/projects/riskmanager/garaujo.space portfolio modulo logos estudos.png', alt: 'Módulo logo studies' },
          ]}
        />

        <hr />

        <section className="project-case">
          {/* <span>{plurallItem && plurallItem.category}</span> */}
          <article className="project-case__intro">
            <h2 id="plurall">{translations.projectPage.caseTitle}</h2>
            <p>{translations.projectPage.caseIntroP1}</p>
            <p>{translations.projectPage.caseIntroP2}</p>
          </article>

          <article className="project-case__details">
            <h3>{translations.projectPage.caseSectionTitle}</h3>
            <p>{translations.projectPage.caseSectionBody1}</p>
            <p>{translations.projectPage.caseSectionBody2}</p>
          </article>
          {plurallItem && (
            <div className="project-case__meta">
              <Button
                size="lg"
                href={plurallItem.caseLink}
                rel="noreferrer noopener"
                ariaLabel={translations.projectPage.casePlurall}
              >
                {translations.projectPage.casePlurall}
              </Button>
              <Button
                size="lg"
                variant='ghost'
                href={plurallItem.link}
                target="_blank"
                rel="noreferrer noopener"
                ariaLabel={translations.projectPage.visitPlurall}
              >
                {translations.projectPage.visitPlurall}
              </Button>
            </div>
          )}
        </section>

        <Gallery
          images={[
            { src: '/img/projects/plurall/portfolio-plurall-icons.png', alt: 'Plurall Ícones v1' },
            { src: '/img/projects/plurall/portfolio-plurall-mascotes-1.png', alt: 'Plurall Mascotes v1' },
            { src: '/img/projects/plurall/portfolio-plurall-mascotes-2.png', alt: 'Plurall Mascotes v1' },
            { src: '/img/projects/plurall/plurall-screens-01.png', alt: 'Plurall' },
            { src: '/img/projects/plurall/plurall-screens-02.png', alt: 'Testing Plurall ARIA support' },
            { src: '/img/projects/plurall/plurall-figma-admin-01.png', alt: 'Plurall Fonts' },
          ]}
        />

          <hr />

        <section className="project-case" id='email-code'>
          <article>
            <h2 id="email-solution">{translations.projectPage.purposeTitle}</h2>
            <p>{translations.projectPage.purposeText1}</p>
            <p>{translations.projectPage.purposeText2}</p>
            <p>{translations.projectPage.purposeText3}</p>
          </article>
          <div className="project-case__meta">
            <Button
              size="lg"
              href="https://medium.com/design-bootcamp/when-purpose-leads-the-way-99d802a8572b"
              target="_blank"
              rel="noreferrer noopener"
              ariaLabel={translations.projectPage.readArticle}
            >
              {translations.projectPage.readArticle}
            </Button>
          </div>
        </section>

        <Gallery
          images={[
            { src: '/img/projects/plurall/growth-email.png', alt: 'String Stacker' }
          ]}
        />

        <hr />

        <section id='elo-copilot' className="project-case project-case--small">
          <article>
            <h2 id="figma-plugins-and-fonts">{translations.projectPage.eloCopilotTitle}</h2>
            <p>{translations.projectPage.eloCopilotDescription}</p>
          </article>
        </section>
        <Gallery
          images={[
            { src: '/img/projects/plurall/plurall-figma-plugin-01.png', alt: 'Figma Plugin Elo Copilot' },
            { src: '/img/projects/plurall/plurall-figma-plugin-02.png', alt: 'Figma Plugin Elo Copilot' },
            { src: '/img/projects/plurall/plurall-screens-03.png', alt: 'Plurall Fonts' },
          ]}
        />


        <hr />

        <section className="project-case" id="linkedin-case">
          <article className="project-case__intro">
            <h2>{translations.projectPage.linkedinCaseTitle}</h2>
            <p>{translations.projectPage.linkedinCaseBody}</p>
          </article>
          <div className="project-case__meta">
            <Button
              size="lg"
              href="https://www.behance.net/gallery/254898693/Case-Study-LinkedIn-as-a-Tool"
              target="_blank"
              rel="noreferrer noopener"
              ariaLabel={translations.projectPage.linkedinCaseButton}
            >
              {translations.projectPage.linkedinCaseButton}
            </Button>
          </div>
        </section>

        <hr />

        <section className="project-cards-section">
          <h2 id="other-projects">{translations.projectPage.otherProjectsTitle}</h2>
          <div className="project-cards">
            {otherProjects.map((project, index) => {
              const projectTitle = localizeField(project.name, language);
              const hasRealLink = !!project.link && project.link !== '#';
              const galleryImages = Array.isArray(project.images) ? project.images : [];
              const cardBody = (
                <div className="project-card__body">
                  <span className='tag'>{localizeField(project.category, language)}</span>
                  <h3>{projectTitle}</h3>
                  {galleryImages.length > 0 ? (
                    <ProjectImageGallery
                      images={galleryImages}
                      projectTitle={projectTitle}
                    />
                  ) : null}
                  <p>{localizeField(project.desc, language)}</p>
                </div>
              );

              return hasRealLink ? (
                <a
                  className="project-card project-card--link"
                  key={`${projectTitle}-${index}`}
                  href={project.link}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {cardBody}
                </a>
              ) : (
                <div
                  className="project-card project-card--disabled"
                  key={`${projectTitle}-${index}`}
                >
                  {cardBody}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}

export default function Projects() {
  const { translations, language } = useI18n();
  const workItems = profileData.work?.items || [];
  const plurallItem = workItems.find((item) => localizeField(item.name, language) === 'Plurall');
  const otherProjects = workItems
    .filter((item) => localizeField(item.name, language) !== 'Plurall')
    .slice()
    .sort((a, b) => {
      const aHasLink = !!a.link && a.link !== '#';
      const bHasLink = !!b.link && b.link !== '#';
      if (aHasLink === bHasLink) return 0;
      return aHasLink ? -1 : 1;
    });

  return (
    <Scroll>
      <main className="page">
        <ProjectsInner
          translations={translations}
          language={language}
          plurallItem={plurallItem}
          otherProjects={otherProjects}
        />
      </main>
    </Scroll>
  );
}

